import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import {
  getSolutionBasePath,
  getSolutionCatalog,
  getSolutionLabel,
  isGatedSolutionPlatform,
  parseSolutionDetailId,
  resolveSolutionEntry,
  SolutionCatalogEntry,
  SolutionKind
} from "../config/solution-catalog";
import { SolutionMenuItem } from "../models/products.model";
import { ProductService } from "./products.service";
import { SiilaTranslationService } from "./siila-translation.service";

/**
 * One ready-to-render solution link. The header, the sidenav, the footer and
 * "Who We Serve" all consume this single shape — including on ungated CO, where
 * it is projected from the CMS records — so the four surfaces cannot present
 * different sets, labels or URLs.
 */
export interface SolutionNavLink {
  id: number;
  /** Localized, `/lang`-suffixed path — hand straight to `routerLink`/`href`. */
  path: string;
  /** Visible label, already resolved to the active language. */
  label: string;
  /** Absent on CO, whose records are not in the catalog. */
  kind: SolutionKind;
}

/**
 * The one way to turn `solution-catalog.ts` data into URLs and labels.
 *
 * On BR/MX/LATAM the list is hardcoded. On CO it is still fetched from the CMS
 * (see `isGatedSolutionPlatform`), projected into the same `SolutionNavLink`
 * shape so callers keep one code path.
 */
@Injectable({ providedIn: "root" })
export class SolutionCatalogService {
  private readonly platform: string = environment.platformId;

  constructor(
    private siilaTranslationService: SiilaTranslationService,
    private productService: ProductService
  ) {}

  /**
   * Whether this deployment gates `/solutions/*` by catalog. `false` on CO,
   * where an unknown id must NOT be treated as a 404 — there is no local list to
   * check it against.
   */
  isGated(): boolean {
    return isGatedSolutionPlatform(this.platform);
  }

  /** Raw entries for this deployment, in render order (empty on CO). */
  getEntries(): SolutionCatalogEntry[] {
    return getSolutionCatalog(this.platform);
  }

  /**
   * The deployment's solution links, localized to `lang` (the active language by
   * default). Emits synchronously from the catalog on a gated platform; on CO it
   * projects the CMS list, so callers subscribe either way and never branch.
   */
  getNavLinks$(lang?: string): Observable<SolutionNavLink[]> {
    const language = lang || this.siilaTranslationService.getCurrentLanguage();
    if (this.isGated()) {
      return of(
        this.getEntries().map(entry => ({
          id: entry.id,
          path: this.buildPath(entry, language),
          label: getSolutionLabel(entry, language),
          kind: entry.kind
        }))
      );
    }
    return this.productService
      .getSolutionList()
      .pipe(map(solutions => this.projectCmsList(solutions, language)));
  }

  /**
   * CO's fallback: the previous CMS-driven behavior, expressed in the shared
   * shape. The slug still comes from `solutionName` and the label from
   * `shortName`, exactly as the four surfaces derived them before.
   */
  private projectCmsList(
    solutions: SolutionMenuItem[],
    language: string
  ): SolutionNavLink[] {
    return (solutions || []).map(solution => ({
      id: solution.id,
      path: this.siilaTranslationService.buildSolutionDetailUrl(
        solution.solutionName,
        solution.id,
        language
      ),
      label: this.siilaTranslationService.getTranslation(solution.shortName),
      kind: null as SolutionKind
    }));
  }

  /** Localized path for one entry — leading slash, `/lang` suffix applied. */
  buildPath(entry: SolutionCatalogEntry, lang?: string): string {
    const language = lang || this.siilaTranslationService.getCurrentLanguage();
    const base = getSolutionBasePath(entry, language);
    return base
      ? this.siilaTranslationService.buildLocalizedPath("/" + base, language)
      : null;
  }

  /**
   * Suffixless base path (`solutions/<id>/<slug>`, no leading slash) for one
   * entry in one language — the shape `MetaTagService.addHreflangTags` expects
   * from its per-language builder.
   */
  buildBasePath(entry: SolutionCatalogEntry, lang: string): string {
    return getSolutionBasePath(entry, lang);
  }

  /**
   * Resolve a requested `solutions/:id/:name` route on a GATED platform.
   *
   * Returns the entry that should render plus its ONE canonical path, or `null`
   * when this market publishes no equivalent — the detail page's cue to answer
   * 404. A foreign id resolves to the local entry for the same solution, which
   * the caller then serves as a 301.
   *
   * Callers must check {@link isGated} first: on CO this always returns `null`,
   * which means "not gated", not "not found".
   */
  resolveRoute(
    rawId: string,
    lang?: string
  ): { entry: SolutionCatalogEntry; canonicalPath: string } {
    const entry = resolveSolutionEntry(this.platform, +rawId);
    if (!entry) {
      return null;
    }
    return { entry, canonicalPath: this.buildPath(entry, lang) };
  }

  /**
   * Which solution a `/solutions/...` path addresses, or `null` when the path is
   * not one this deployment publishes. Lets meta-tag dispatch key off the
   * solution rather than matching ids market by market.
   */
  getKindForPath(path: string): SolutionKind {
    const id = parseSolutionDetailId(path);
    if (id == null) {
      return null;
    }
    const entry = resolveSolutionEntry(this.platform, id);
    return entry ? entry.kind : null;
  }
}
